import React, { useState } from "react";
import MediaPicker from "@/admin/MediaPicker";
import { resolveMediaSrc } from "@/lib/api";
import { ImagePlus, Film, FileText } from "lucide-react";

export const FieldLabel = ({ children }) => (
  <label className="block text-[11px] tracking-wide uppercase text-zinc-500 mb-1">{children}</label>
);

export const TextField = ({ label, value, onChange, multiline, testid }) => (
  <div>
    {label && <FieldLabel>{label}</FieldLabel>}
    {multiline ? (
      <textarea
        data-testid={testid}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={Math.min(8, Math.max(2, String(value || "").split("\n").length + 1))}
        className="w-full px-3 py-2 border border-zinc-300 rounded text-sm focus:outline-none focus:border-zinc-900"
      />
    ) : (
      <input
        data-testid={testid}
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-zinc-300 rounded text-sm focus:outline-none focus:border-zinc-900"
      />
    )}
  </div>
);

export const MediaField = ({ label, value, onChange, kind = "image", testid }) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const renderPreview = () => {
    if (!value) {
      return (
        <div className="w-16 h-10 bg-zinc-100 border border-dashed border-zinc-300 rounded flex items-center justify-center text-zinc-400">
          {kind === "video" ? <Film size={16} /> : kind === "doc" ? <FileText size={16} /> : <ImagePlus size={16} />}
        </div>
      );
    }
    if (kind === "video") return <video src={resolveMediaSrc(value)} className="w-16 h-10 object-cover rounded" muted />;
    if (kind === "doc")
      return (
        <a href={resolveMediaSrc(value)} target="_blank" rel="noreferrer" title="Open file" className="w-16 h-10 bg-[#942222]/5 border border-zinc-200 rounded flex items-center justify-center text-[#942222]">
          <FileText size={16} />
        </a>
      );
    return <img src={resolveMediaSrc(value)} alt="" className="w-16 h-10 object-cover rounded" />;
  };

  const placeholder = kind === "doc" ? "Paste menu URL or pick PDF/photo" : `Paste ${kind} URL or pick from library`;

  return (
    <div>
      {label && <FieldLabel>{label}</FieldLabel>}
      <div className="flex items-stretch gap-3">
        <div className="flex-1 flex items-center gap-3 border border-zinc-300 rounded px-2 py-2 bg-white">
          {renderPreview()}
          <input
            data-testid={testid}
            type="text"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="flex-1 text-xs px-2 py-1 focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="px-3 py-2 text-xs bg-zinc-900 text-white rounded hover:bg-zinc-800"
          data-testid={testid ? `${testid}-pick` : undefined}
        >
          Library
        </button>
      </div>
      <MediaPicker open={pickerOpen} kind={kind} onClose={() => setPickerOpen(false)} onPick={onChange} />
    </div>
  );
};
